import React, { useEffect, useState } from 'react';
import { useOnboardingGuide, ONBOARDING_STEPS } from '../../hooks/useOnboardingGuide';

interface OnboardingProgressToastProps {
  className?: string;
}

const OnboardingProgressToast: React.FC<OnboardingProgressToastProps> = ({ className = '' }) => {
  const {
    showStepCompleteToast,
    recentlyCompletedStep,
    nextStep,
    progress,
    essentialProgress,
    dismissStepCompleteToast,
    goToNextStep,
    isComplete
  } = useOnboardingGuide();

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (showStepCompleteToast && recentlyCompletedStep) {
      setIsVisible(true);
      setIsAnimating(true);
      
      // Auto dismiss after 8 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 8000);

      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [showStepCompleteToast, recentlyCompletedStep]);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      dismissStepCompleteToast();
      setIsVisible(false);
    }, 300);
  };

  const handleContinueClick = () => {
    handleDismiss();
    if (nextStep) {
      goToNextStep();
    }
  };

  const handlePostponeClick = () => {
    handleDismiss();
  };

  if (!isVisible || !recentlyCompletedStep) {
    return null;
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 max-w-sm w-full mx-4 sm:mx-0 ${className}`}>
      <div 
        className={`transform transition-all duration-300 ease-out ${
          isAnimating 
            ? 'translate-y-0 opacity-100 scale-100' 
            : 'translate-y-4 opacity-0 scale-95'
        }`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 dark:ring-gray-700 overflow-hidden">
          {/* Header with celebration */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    ¡Paso completado!
                  </p>
                  <p className="text-xs text-green-100">
                    {progress}% del onboarding terminado
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white hover:text-green-100 transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Completed step info */}
            <div className="mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {recentlyCompletedStep.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Completado correctamente
                  </p>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Progreso general
                </span>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-700 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span>Esenciales: {essentialProgress.completed}/{essentialProgress.total}</span>
                <span>Total: {Math.round(progress/100 * ONBOARDING_STEPS.length)}/{ONBOARDING_STEPS.length}</span>
              </div>
            </div>

            {/* Next step or completion */}
            {isComplete ? (
              <div className="text-center">
                <div className="mb-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    ¡Onboarding completado!
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Ya tienes todo configurado para usar GuideMe
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="w-full px-3 py-2 bg-gradient-to-r from-blue-700 to-pink-600 text-white text-sm font-medium rounded-lg hover:from-blue-800 hover:to-pink-700 transition-all duration-200"
                >
                  ¡Empezar a usar GuideMe!
                </button>
              </div>
            ) : nextStep ? (
              <div>
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Siguiente paso:
                  </p>
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      {nextStep.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {nextStep.title}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {nextStep.description}
                      </p>
                      {nextStep.optional && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 mt-1">
                          Opcional
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={handleContinueClick}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                  >
                    <span>Continuar</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={handlePostponeClick}
                    className="px-3 py-2 text-gray-600 dark:text-gray-400 text-sm hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  >
                    Después
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  ¡Excelente progreso!
                </p>
                <button
                  onClick={handleDismiss}
                  className="mt-2 px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingProgressToast;